import React, { useState, useCallback, useMemo, useContext } from "react"
import { Box } from "@mui/material"
import { useSelector, useDispatch } from "react-redux"
import { RootState } from "../../redux/store"
import { updateAnnouncements } from "../../redux/modules/users"
import { AnnouncementsItemType } from "../../../mp-database/contextHelpManager"
import { AnnouncementsBanner } from "./components"
import { SnackbarProps } from "../../../mp-shared-components/generic-shared-components/components"
import { useLocation } from "react-router-dom"
import { checkValidityAnnouncement } from "./helpers"
import { ProgramsContext } from "../../../mp-shared-components/ready-shared-components/components/Navs"

interface Props {
  placement: "TOP" | "BODY"
  location?: string
  program?: string
  excludeRoutes?: string[]
}

const AnnouncementsBannerContainer: React.FC<
  React.PropsWithChildren<Props>
> = ({ placement, location, program, excludeRoutes = [] }) => {
  const { currentProgram, programsAndCoursesLoading } =
    useContext(ProgramsContext)

  const [index, setIndex] = useState(0)

  const dispatch = useDispatch() as any

  const currentLocation = useLocation()

  const { user, announcements = [] } = useSelector((state: RootState) => ({
    user: state.users.testUser,
    announcements: state.users.announcements as AnnouncementsItemType[]
  }))

  // Check if current route should be excluded
  const shouldExclude = useMemo(() => {
    return excludeRoutes.some(pattern => {
      const regex = new RegExp(pattern.replace(/\*/g, ".*"))
      return regex.test(currentLocation.pathname)
    })
  }, [excludeRoutes, currentLocation.pathname])

  const bannerAnnouncements = useMemo(() => {
    if (!placement) return []

    let filteredAnnouncements: AnnouncementsItemType[] = announcements.filter(
      a =>
        checkValidityAnnouncement({
          announcementTypes: ["ALERT"],
          announcement: a,
          user: user,
          userPage: currentLocation.pathname,
          userProgram: currentProgram
        })
    )

    if (placement === "TOP") {
      filteredAnnouncements = filteredAnnouncements.filter(
        a => a?.placement === "TOP"
      )
    } else {
      filteredAnnouncements = filteredAnnouncements.filter(
        a => a?.placement === placement
      )

      if (location) {
        filteredAnnouncements = filteredAnnouncements.filter(a =>
          a?.locations?.includes(location)
        )
      }
      if (program) {
        filteredAnnouncements = filteredAnnouncements.filter(a =>
          a?.programs?.includes(program.toUpperCase())
        )
      }
    }

    return filteredAnnouncements
  }, [
    announcements,
    placement,
    location,
    program,
    currentLocation,
    currentProgram,
    programsAndCoursesLoading
  ])

  const currentBanner = bannerAnnouncements?.[index]

  const handleConfirm = useCallback(async () => {
    if (currentBanner) {
      if (bannerAnnouncements?.length - 1 === index) {
        setIndex(prevIndex => {
          return prevIndex - 1 < 0 ? 0 : prevIndex - 1
        })
      }

      const aIdx = announcements.findIndex(a => a.id === currentBanner.id)

      if (aIdx > -1) {
        await dispatch(updateAnnouncements(user, announcements[aIdx]))
      }
    }
  }, [index, currentBanner, bannerAnnouncements, announcements, user?.id])

  if (shouldExclude || !bannerAnnouncements?.length) return null

  const bannerNode = (
    <AnnouncementsBanner
      severity={
        (currentBanner?.tone?.toLowerCase() as SnackbarProps["severity"]) ||
        "info"
      }
      message={currentBanner?.message || ""}
      currentBannerIndex={index}
      totalBanners={bannerAnnouncements?.length}
      allowPermanentDismiss={!!currentBanner?.allowPermanentDismiss}
      onDismiss={handleConfirm}
      onChange={(nextBannerIndex: number) => setIndex(nextBannerIndex)}
    />
  )

  return placement === "BODY" ? (
    <Box mt={2} mb={6}>
      {bannerNode}
    </Box>
  ) : (
    bannerNode
  )
}

export default AnnouncementsBannerContainer
